import { useId, useState, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { PASSWORD_MIN_LENGTH } from '@cercle/shared';
import { Input } from '@/shared/ui';
import styles from './auth.module.scss';

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  required?: boolean;
  /** Affiche la longueur restante — utile à la création, gênant à la connexion. */
  showStrengthHint?: boolean;
}

/**
 * Champ de mot de passe avec bascule d'affichage.
 *
 * La bascule n'est pas un gadget : sur mobile, saisir douze caractères en aveugle au pouce
 * est la première cause d'échec de connexion. Le bouton est un vrai `<button>` avec
 * `aria-pressed`, il reste donc accessible au clavier et annoncé correctement.
 */
export function PasswordInput({
  value,
  onChange,
  autoComplete,
  required,
  showStrengthHint = false,
}: PasswordInputProps): ReactNode {
  const [visible, setVisible] = useState(false);
  const hintId = useId();
  const remaining = PASSWORD_MIN_LENGTH - value.length;

  return (
    <>
      <span className={styles.passwordField}>
        <Input
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          aria-describedby={showStrengthHint ? hintId : undefined}
        />
        <button
          type="button"
          className={styles.reveal}
          aria-pressed={visible}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          onClick={() => setVisible((current) => !current)}
        >
          {visible ? (
            <EyeOff size={17} strokeWidth={2.75} aria-hidden="true" />
          ) : (
            <Eye size={17} strokeWidth={2.75} aria-hidden="true" />
          )}
        </button>
      </span>

      {showStrengthHint ? (
        <span id={hintId} className={styles.availability}>
          {remaining > 0
            ? `Encore ${remaining} caractère${remaining > 1 ? 's' : ''} — visez une phrase, pas un mot.`
            : 'Longueur suffisante.'}
        </span>
      ) : null}
    </>
  );
}
